import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import Icon from '../../../../atoms/Icon'
import FlexRow from '../../../../molecules/FlexRow'
import { LinkedContactRow } from '../shared/LinkedContactDisplay'
import ActivityDetailModal from '../shared/ActivityDetailModal'
import CreateActivityModal from '../shared/CreateActivityModal'
import { getTaskStatus } from '../shared/redtailUIHelpers'
import useCategoryOptions from './useCategoryOptions'

const LIST_INVALIDATE_KEYS = [[QUERY_KEYS.redtailDomainQuery, 'list-preset']]

const TASK_STATUS = {
  completed: { icon: 'checkCircle', color: 'var(--color-growth-500)' },
  overdue: { icon: 'clock', color: 'var(--color-decline-500)' },
  outstanding: { icon: 'radioUnchecked', color: 'var(--text-color-subtle)' }
}

function TaskRow ({ activity, sizes, onClick }) {
  const status = getTaskStatus(activity)
  const cfg = TASK_STATUS[status]
  const dueStr = activity.end_date ? dayjs(activity.end_date).format('M/D/YYYY') : null

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      style={{ borderBottom: '1px solid var(--color-neutral-200)', padding: sizes.rowPadding, cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          paddingTop: '2px',
          color: cfg.color
        }}
        >
          <Icon name={cfg.icon} customSize='20px' />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-color-subtle)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            <LinkedContactRow contacts={activity.linked_contacts} />
          </div>
          <div style={{
            fontSize: sizes.fontSize,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            {activity.subject || 'Untitled Task'}
          </div>
        </div>
        {dueStr ? (
          <div style={{
            flexShrink: 0,
            textAlign: 'right',
            fontSize: 'var(--font-size-xs)',
            color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)',
            whiteSpace: 'nowrap'
          }}
          >
            {status === 'completed' ? 'Completed' : `Due ${dueStr}`}
          </div>
        ) : null}
      </div>
    </div>
  )
}

TaskRow.propTypes = {
  activity: PropTypes.object.isRequired,
  sizes: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
}

function TasksContent ({ items, sizes, members, config, enableCreate, enableEdit, formMode, createOpen, onCloseCreate }) {
  const { clientId } = useAppContext()
  const categoryOptions = useCategoryOptions(clientId, config?.categoryIds)
  const [selectedActivity, setSelectedActivity] = useState(null)

  const handleCloseDetail = useCallback(() => setSelectedActivity(null), [])

  const defaultActivityCodeId = config?.activityCodeIds?.[0] ?? 1

  return (
    <>
      {!items.length
        ? (
          <FlexRow
            className='justify-center items-center'
            style={{
              minHeight: '80px',
              color: 'var(--text-color-subtle)',
              fontSize: 'var(--font-size-sm)'
            }}
          >
            No tasks found
          </FlexRow>
        )
        : items.map(activity => (
          <TaskRow
            key={activity.id}
            activity={activity}
            sizes={sizes}
            onClick={() => setSelectedActivity(activity)}
          />
        ))}

      {enableCreate ? (
        <CreateActivityModal
          open={createOpen}
          onClose={onCloseCreate}
          config={config}
          members={members}
          formMode={formMode}
          defaultActivityCodeId={defaultActivityCodeId}
          createLabel='Create Task'
          invalidateQueryKeys={LIST_INVALIDATE_KEYS}
          categoryOptions={categoryOptions}
        />
      ) : null}

      <ActivityDetailModal
        open={!!selectedActivity}
        onClose={handleCloseDetail}
        activity={selectedActivity}
        config={config}
        members={members}
        enableEdit={enableEdit}
        formMode={formMode}
        invalidateQueryKeys={LIST_INVALIDATE_KEYS}
        categoryOptions={categoryOptions}
      />
    </>
  )
}

TasksContent.propTypes = {
  items: PropTypes.array.isRequired,
  sizes: PropTypes.object.isRequired,
  members: PropTypes.array,
  config: PropTypes.object,
  enableCreate: PropTypes.bool,
  enableEdit: PropTypes.bool,
  formMode: PropTypes.string,
  createOpen: PropTypes.bool,
  onCloseCreate: PropTypes.func
}

export default TasksContent
